import "@/App.css";
import "@/altiora-premium.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";
import { CapabilitiesProvider } from "@/lib/capabilities";
import { I18nProvider } from "@/lib/i18n";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import AppShell from "@/components/layout/AppShell";
import Dashboard from "@/pages/Dashboard";
import ModulesGrid from "@/pages/ModulesGrid";
import Profile from "@/pages/Profile";
import CareerIntelligence from "@/pages/CareerIntelligence";
import Opportunities from "@/pages/Opportunities";
import Assistant from "@/pages/Assistant";
import FounderConsole from "@/pages/FounderConsole";
import { Vault, Documents, Tracker, CalendarPage, HiddenOpportunityEngine, DecisionEngine, ConnectorEngine, CareerSimulation, KnowledgeGraph, LearningMemory, GlobalMobility } from "@/pages/Modules";
import PartnerRegister from "@/pages/PartnerRegister";
import PartnerConsole from "@/pages/PartnerConsole";
import PartnerDirectory from "@/pages/PartnerDirectory";
import LifeStrategy from "@/pages/LifeStrategy";
import TrustCenter from "@/pages/TrustCenter";
import Reports from "@/pages/Reports";
import WhiteLabelStudio from "@/pages/WhiteLabelStudio";
import EnterpriseAPI from "@/pages/EnterpriseAPI";
import SharedReport from "@/pages/SharedReport";
import Finance from "@/pages/Finance";
import FinanceSuccess from "@/pages/FinanceSuccess";
import Governance from "@/pages/Governance";
import AdminCenter from "@/pages/AdminCenter";
import AdminConsole from "@/pages/AdminConsole";
import GeoGps from "@/pages/GeoGps";
import Alerts from "@/pages/Alerts";
import Professionals from "@/pages/Professionals";
import Priorities from "@/pages/Priorities";
import Discovery from "@/pages/Discovery";
import Knowledge from "@/pages/Knowledge";
import KnowledgeSources from "@/pages/KnowledgeSources";
import Today from "@/pages/Today";
import Network from "@/pages/Network";
import Communities from "@/pages/Communities";
import Library from "@/pages/Library";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AdminUsers from "@/pages/AdminUsers";
import AdminDocs from "@/pages/AdminDocs";
import Studio from "@/pages/Studio";
import Interview from "@/pages/Interview";
import ProLogin from "@/pages/ProLogin";
import ProDashboard from "@/pages/ProDashboard";
import ProfessionalRegister from "@/pages/ProfessionalRegister";
import MentorDashboard from "@/pages/MentorDashboard";
import Diagnostic from "@/pages/Diagnostic";
import DiagnosticReport from "@/pages/DiagnosticReport";
import DossierNew from "@/pages/DossierNew";
import DossierDetail from "@/pages/DossierDetail";
import DossierOpportunities from "@/pages/DossierOpportunities";
import DossierCareers from "@/pages/DossierCareers";
import DossierQuestionnaire from "@/pages/DossierQuestionnaire";
import EliaBrandPreview from "@/pages/EliaBrandPreview";
import EliaHome from "@/pages/EliaHome";
import EliaModulePage from "@/pages/EliaModulePage";
import InstitutionPortal from "@/pages/InstitutionPortal";
import JoinFromInvite from "@/pages/JoinFromInvite";
import JoinFromProConsultation from "@/pages/JoinFromProConsultation";
import PublicSecurity from "@/pages/PublicSecurity";
import AccountSettings from "@/pages/AccountSettings";
import IntelligenceHub from "@/pages/Intelligence/IntelligenceHub";
import AbiRadar from "@/pages/ABI/AbiRadar";
import AbiDashboard from "@/pages/ABI/AbiDashboard";
import AbiActions from "@/pages/ABI/AbiActions";
import AbiPipeline from "@/pages/ABI/AbiPipeline";
import AbiTrajectory from "@/pages/ABI/AbiTrajectory";
import AbiLearning from "@/pages/ABI/AbiLearning";
import AbiCoach from "@/pages/ABI/AbiCoach";
import AbiExcellence from "@/pages/ABI/AbiExcellence";
import AbiPassport from "@/pages/ABI/AbiPassport";
import AbiOnboarding from "@/pages/ABI/AbiOnboarding";
import AbiCareer from "@/pages/ABI/AbiCareer";
import IntelCommerciale from "@/pages/IntelCommerciale";
import OpportunityInbox from "@/pages/OpportunityInbox";
import NetworkAndScan from "@/pages/NetworkAndScan";
import NavigateurEconomique from "@/pages/NavigateurEconomique";
import DesignSystem from "@/pages/DesignSystem";
import AbiKnowledge from "@/pages/ABI/AbiKnowledge";
import AbiJournal from "@/pages/ABI/AbiJournal";
import AbiDoctrine from "@/pages/ABI/AbiDoctrine";
import TargetList from "@/pages/Intelligence/TargetList";
import TargetPipeline from "@/pages/Intelligence/TargetPipeline";
import AdvisorStrategy from "@/pages/Intelligence/AdvisorStrategy";
import ComplianceCenter from "@/pages/Intelligence/ComplianceCenter";
import TargetImport from "@/pages/Intelligence/TargetImport";
import TargetOcrCard from "@/pages/Intelligence/TargetOcrCard";
import EcosystemMap from "@/pages/Intelligence/EcosystemMap";
import ClientsStudio from "@/pages/ClientsStudio";
import AdaptiveHome from "@/pages/AdaptiveHome";
import SharedUnifiedBilan from "@/pages/SharedUnifiedBilan";
import AdminInstitutionsEditor from "@/pages/AdminInstitutionsEditor";
import AdminPdfMonitoring from "@/pages/AdminPdfMonitoring";
import SalesOS from "@/pages/SalesOS";
import RevenueDashboard from "@/pages/RevenueDashboard";
import AdminSalesConfig from "@/pages/AdminSalesConfig";
import GamificationPage from "@/pages/GamificationPage";
import CoachCsf from "@/pages/CoachCsf";
import Lms from "@/pages/Lms";
import ODE from "@/pages/ODE";
import AdminOdeConfig from "@/pages/AdminOdeConfig";
import DigitalTwin from "@/pages/DigitalTwin";
import MorningBriefing from "@/pages/MorningBriefing";
import Simulator from "@/pages/Simulator";
import AdvisorMemory from "@/pages/AdvisorMemory";
import IntegrationsHub from "@/pages/IntegrationsHub";
import BiLeader from "@/pages/BiLeader";
import AdminLeads from "@/pages/AdminLeads";
import AdminGeoConfig from "@/pages/AdminGeoConfig";
import AdminKnowledgeEngine from "@/pages/AdminKnowledgeEngine";
import AdminKnowledgeGraph from "@/pages/AdminKnowledgeGraph";
import AdminKnowledgeIngestion from "@/pages/AdminKnowledgeIngestion";
import AdminAltioraIQ from "@/pages/AdminAltioraIQ";
import AdminGrowthMedia from "@/pages/AdminGrowthMedia";
import GrowthRadar from "@/pages/GrowthRadar";
import AltioraRadar from "@/pages/AltioraRadar";
import SourcesManager from "@/pages/SourcesManager";
import FounderValueTest from "@/pages/FounderValueTest";
import AdminAvatarOnboarding from "@/pages/AdminAvatarOnboarding";
import ImpactToday from "@/pages/ImpactToday";
import AdminPressKit from "@/pages/AdminPressKit";
import OnboardingDiagnostic from "@/pages/OnboardingDiagnostic";
import DemoRequest from "@/pages/DemoRequest";
import Manifeste from "@/pages/Manifeste";
import PublicInsights from "@/pages/PublicInsights";
import AltioraLeads from "@/pages/AltioraLeads";
import AltioraRadarPersons from "@/pages/AltioraRadarPersons";
import AltioraCampaigns from "@/pages/AltioraCampaigns";
import CampaignLanding from "@/pages/CampaignLanding";

function AppRouter() {
  const location = useLocation();
  // Intercept OAuth callback fragment synchronously before other routes render.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/onboarding" element={<OnboardingDiagnostic />} />
      <Route path="/demo" element={<DemoRequest />} />
      <Route path="/manifeste" element={<Manifeste />} />
      <Route path="/insights" element={<PublicInsights />} />
      <Route path="/securite" element={<PublicSecurity />} />
      <Route path="/brand/elia" element={<EliaBrandPreview />} />
      <Route path="/elia" element={<EliaHome />} />
      <Route path="/elia/:slug" element={<EliaModulePage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/register" element={<Register />} />
      <Route path="/join" element={<JoinFromInvite />} />
      <Route path="/consult" element={<JoinFromProConsultation />} />
      <Route path="/join/membre" element={<Register />} />
      <Route path="/join/professionnel" element={<ProfessionalRegister />} />
      <Route path="/join/partenaire" element={<PartnerRegister />} />
      <Route path="/partner/register" element={<PartnerRegister />} />
      <Route path="/share/:token" element={<SharedReport />} />
      <Route path="/c/:slug" element={<CampaignLanding />} />
      <Route path="/share/unified/:token" element={<SharedUnifiedBilan />} />
      <Route path="/pro/login" element={<ProLogin />} />
      <Route path="/pro" element={<ProDashboard />} />
      <Route path="/app" element={<AppShell />}>
        <Route index element={<MorningBriefing />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="home" element={<AdaptiveHome />} />
        <Route path="briefing" element={<MorningBriefing />} />
        <Route path="simulator" element={<Simulator />} />
        <Route path="memory" element={<AdvisorMemory />} />
        <Route path="integrations" element={<IntegrationsHub />} />
        <Route path="bi" element={<BiLeader />} />
        <Route path="admin/leads" element={<AdminLeads />} />
        <Route path="altiora-leads" element={<AltioraLeads />} />
        <Route path="altiora-radar" element={<AltioraRadar />} />
        <Route path="altiora-radar/persons" element={<AltioraRadarPersons />} />
        <Route path="campaigns" element={<AltioraCampaigns />} />
        <Route path="admin/geo" element={<AdminGeoConfig />} />
        <Route path="admin/knowledge-engine" element={<AdminKnowledgeEngine />} />
        <Route path="admin/knowledge-graph" element={<AdminKnowledgeGraph />} />
        <Route path="admin/knowledge-ingestion" element={<AdminKnowledgeIngestion />} />
        <Route path="admin/altiora-iq" element={<AdminAltioraIQ />} />
        <Route path="admin/growth-media" element={<AdminGrowthMedia />} />
        <Route path="admin/growth-radar" element={<GrowthRadar />} />
        <Route path="radar" element={<AltioraRadar />} />
        <Route path="radar/sources" element={<SourcesManager />} />
        <Route path="radar/value-test" element={<FounderValueTest />} />
        <Route path="admin/avatar-onboarding" element={<AdminAvatarOnboarding />} />
        <Route path="impact" element={<ImpactToday />} />
        <Route path="admin/press-kit" element={<AdminPressKit />} />
        <Route path="modules" element={<ModulesGrid />} />
        <Route path="finance" element={<Finance />} />
        <Route path="finance/success" element={<FinanceSuccess />} />
        <Route path="governance" element={<Governance />} />
        <Route path="admin" element={<AdminConsole />} />
        <Route path="admin/institutions" element={<AdminInstitutionsEditor />} />
        <Route path="admin/pdf-monitoring" element={<AdminPdfMonitoring />} />
        <Route path="admin/sales-config" element={<AdminSalesConfig />} />
        <Route path="sales" element={<SalesOS />} />
        <Route path="revenue" element={<RevenueDashboard />} />
        <Route path="gamification" element={<GamificationPage />} />
        <Route path="coach" element={<CoachCsf />} />
        <Route path="lms" element={<Lms />} />
        <Route path="ode" element={<ODE />} />
        <Route path="admin/ode-config" element={<AdminOdeConfig />} />
        <Route path="twin" element={<DigitalTwin />} />
        <Route path="institution" element={<InstitutionPortal />} />
        <Route path="admin/legacy" element={<AdminCenter />} />
        <Route path="admin/users" element={<AdminUsers />} />
        <Route path="admin/docs" element={<AdminDocs />} />
        <Route path="gps" element={<GeoGps />} />
        <Route path="alerts" element={<Alerts />} />
        <Route path="professionals" element={<Professionals />} />
        <Route path="priorities" element={<Priorities />} />
        <Route path="discovery" element={<Discovery />} />
        <Route path="knowledge" element={<Knowledge />} />
        <Route path="knowledge-sources" element={<KnowledgeSources />} />
        <Route path="today" element={<Today />} />
        <Route path="network" element={<Network />} />
        <Route path="mentor" element={<MentorDashboard />} />
        <Route path="diagnostic" element={<Diagnostic />} />
        <Route path="hub" element={<EliaHome />} />
        <Route path="diagnostic/report" element={<DiagnosticReport />} />
        <Route path="dossiers/new" element={<DossierNew />} />
        <Route path="dossiers/:dossier_id" element={<DossierDetail />} />
        <Route path="dossiers/:dossier_id/opportunites" element={<DossierOpportunities />} />
        <Route path="dossiers/:dossier_id/carriere" element={<DossierCareers />} />
        <Route path="dossiers/:dossier_id/questionnaire" element={<DossierQuestionnaire />} />
        <Route path="communities" element={<Communities />} />
        <Route path="library" element={<Library />} />
        <Route path="studio" element={<Studio />} />
        <Route path="interview" element={<Interview />} />
        <Route path="life-strategy" element={<LifeStrategy />} />
        <Route path="trust" element={<TrustCenter />} />
        <Route path="reports" element={<Reports />} />
        <Route path="white-label" element={<WhiteLabelStudio />} />
        <Route path="enterprise-api" element={<EnterpriseAPI />} />
        <Route path="profile" element={<Profile />} />
        <Route path="account" element={<AccountSettings />} />
        <Route path="intelligence" element={<CareerIntelligence />} />
        <Route path="commercial" element={<IntelligenceHub />} />
        <Route path="abi" element={<AbiDashboard />} />
        <Route path="abi/radar" element={<AbiRadar />} />
        <Route path="abi/actions" element={<AbiActions />} />
        <Route path="abi/pipeline" element={<AbiPipeline />} />
        <Route path="abi/trajectory" element={<AbiTrajectory />} />
        <Route path="abi/learning" element={<AbiLearning />} />
        <Route path="abi/coach" element={<AbiCoach />} />
        <Route path="abi/excellence" element={<AbiExcellence />} />
        <Route path="abi/passport" element={<AbiPassport />} />
        <Route path="abi/career" element={<AbiCareer />} />
        <Route path="intel-commerciale" element={<IntelCommerciale />} />
        <Route path="inbox" element={<OpportunityInbox />} />
        <Route path="reseau" element={<NetworkAndScan />} />
        <Route path="navigateur" element={<NavigateurEconomique />} />
        <Route path="design-system" element={<DesignSystem />} />
        <Route path="abi/onboarding" element={<AbiOnboarding />} />
        <Route path="abi/knowledge" element={<AbiKnowledge />} />
        <Route path="abi/journal" element={<AbiJournal />} />
        <Route path="abi/doctrine" element={<AbiDoctrine />} />
        <Route path="commercial/list" element={<TargetList />} />
        <Route path="commercial/pipeline" element={<TargetPipeline />} />
        <Route path="commercial/strategy" element={<AdvisorStrategy />} />
        <Route path="commercial/dnc" element={<ComplianceCenter />} />
        <Route path="commercial/import" element={<TargetImport />} />
        <Route path="commercial/ocr" element={<TargetOcrCard />} />
        <Route path="commercial/map" element={<EcosystemMap />} />
        <Route path="pro/clients" element={<ClientsStudio />} />
        <Route path="opportunities" element={<Opportunities />} />
        <Route path="hidden-opportunities" element={<HiddenOpportunityEngine />} />
        <Route path="decision" element={<DecisionEngine />} />
        <Route path="simulation" element={<CareerSimulation />} />
        <Route path="mobility" element={<GlobalMobility />} />
        <Route path="graph" element={<KnowledgeGraph />} />
        <Route path="memory" element={<LearningMemory />} />
        <Route path="vault" element={<Vault />} />
        <Route path="documents" element={<Documents />} />
        <Route path="tracker" element={<Tracker />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="assistant" element={<Assistant />} />
        <Route path="connectors" element={<ConnectorEngine />} />
        <Route path="partner" element={<PartnerConsole />} />
        <Route path="partner-directory" element={<PartnerDirectory />} />
        <Route path="founder" element={<FounderConsole />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <I18nProvider>
          <AuthProvider>
            <CapabilitiesProvider>
              <AppRouter />
              <Toaster
                theme="light"
                position="bottom-right"
                offset={20}
                closeButton
                richColors={false}
                visibleToasts={4}
                toastOptions={{
                  className: "altiora-toast",
                  style: {
                    background:  "#FFFFFF",
                    border:      "1px solid rgba(197, 168, 128, 0.35)",
                    color:       "#0B2545",
                    fontFamily:  "Satoshi, Inter, sans-serif",
                    fontSize:    "13px",
                    borderRadius: "12px",
                    boxShadow:   "0 12px 32px -12px rgba(11, 37, 69, 0.18)",
                  },
                }}
              />
            </CapabilitiesProvider>
          </AuthProvider>
        </I18nProvider>
      </BrowserRouter>
    </div>
  );
}
